import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  Snackbar
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkIcon from '@mui/icons-material/Link';

const OfferSuccessDialog = ({ open, onClose, offerUrl, offerType = 'offer' }) => {
  const { t } = useTranslation();
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(offerUrl);
      setCopySuccess(true);
    } catch (err) {
      // Fallback for older browsers
      const textField = document.createElement('textarea');
      textField.innerText = offerUrl;
      document.body.appendChild(textField);
      textField.select();
      document.execCommand('copy');
      textField.remove();
      setCopySuccess(true);
    }
  };

  const handleOpenInNewTab = () => {
    window.open(offerUrl, '_blank', 'noopener,noreferrer');
  };

  const handleSnackbarClose = () => {
    setCopySuccess(false);
  };

  const isComparison = offerType === 'comparison';

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
            minWidth: { xs: 'auto', sm: '600px' }
          }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CheckCircleIcon sx={{ color: '#00C853', fontSize: 32 }} />
            <Box>
              <Typography variant="h6" component="div">
                {isComparison 
                  ? t('offers.comparisonGenerated', 'Comparison Generated!')
                  : t('offers.offerGenerated', 'Offer Generated!')
                }
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isComparison
                  ? t('offers.comparisonGeneratedDescription', 'Your pricing comparison has been created successfully')
                  : t('offers.offerGeneratedDescription', 'Your pricing offer has been created successfully')
                }
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              {isComparison 
                ? t('offers.comparisonLink', 'Comparison Link:')
                : t('offers.offerLink', 'Offer Link:')
              }
            </Typography>
            <TextField
              fullWidth
              value={offerUrl}
              variant="outlined"
              size="small"
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleCopyUrl}
                      size="small"
                      sx={{
                        color: '#00C853',
                        '&:hover': {
                          bgcolor: 'rgba(0, 200, 83, 0.08)'
                        }
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.12)',
                    },
                  },
                }
              }}
              sx={{ mb: 2 }}
            />
          </Box>

          <Alert 
            severity="info" 
            sx={{ 
              mb: 2,
              '& .MuiAlert-icon': {
                color: '#00C853'
              }
            }}
          >
            <Typography variant="body2">
              {isComparison
                ? t('offers.comparisonShareInfo', 'Share this link with your clients to show them the comparison of different pricing options.')
                : t('offers.offerShareInfo', 'Share this link with your clients to show them the detailed pricing offer.')
              }
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            onClick={onClose}
            sx={{ 
              color: 'text.secondary',
              minWidth: 120,
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            {t('common.close', 'Close')}
          </Button>
          <Button
            onClick={handleCopyUrl}
            startIcon={<ContentCopyIcon />}
            variant="outlined"
            sx={{
              borderColor: '#00C853',
              color: '#00C853',
              minWidth: 200,
              px: 3,
              py: 1,
              '&:hover': {
                borderColor: '#00A040',
                color: '#00A040',
                bgcolor: 'rgba(0, 200, 83, 0.04)'
              }
            }}
          >
            {t('offers.copyLink', 'Copy Link')}
          </Button>
          <Button
            onClick={handleOpenInNewTab}
            startIcon={<OpenInNewIcon />}
            variant="contained"
            sx={{
              bgcolor: '#00C853',
              minWidth: 220,
              px: 3,
              py: 1,
              '&:hover': {
                bgcolor: '#00A040',
              }
            }}
          >
            {t('offers.openInNewTab', 'Open in New Tab')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ 
            width: '100%',
            '& .MuiAlert-icon': {
              color: '#00C853'
            }
          }}
        >
          {t('offers.linkCopied', 'Link copied to clipboard!')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default OfferSuccessDialog;